/**
 * **Temporary injunction (TK-13530):** purchase designer still authenticates via Magento/ACCS
 * (`sp_auth`). Callers use these helpers to skip Better Auth bootstrap, API traffic, and profile
 * sync on those URLs until ecommerce designer cutover to Better Auth. Delete the gate (this module,
 * `auth-client` stubs, resume plugin, injunction util, and `useBetterAuth` overrides) once the
 * purchase flow uses Better Auth like handwriting designer.
 */

/** `:type` values on `/designer/{start,personalize}/:type/...` — not legacy SKU/id segments. */
const DESIGNER_FLOW_TYPE_SEGMENTS = new Set(['purchase', 'handwriting', 'offtheshelf'])

/**
 * True when the URL is the ecommerce designer journey (stationery purchase) under the injunction
 * above — not the Better Auth–backed handwriting/custom flows.
 *
 * Covers canonical `.../purchase/...` paths and legacy redirects that land on purchase:
 * `/designer/start/:sku/:configTemplateId` and `/designer/personalize/:designProjectId`.
 */
export function isEcommerceDesignerPath(path: string): boolean {
    const pathname = path.split('?')[0]?.split('#')[0] ?? path

    if (pathname.includes('/designer/start/purchase/')) {
        return true
    }
    if (pathname.includes('/designer/personalize/purchase/')) {
        return true
    }

    // Legacy paths use SKU / design-project ids, not flow-type literals (`purchase`, etc.).
    const legacyStart = pathname.match(/^\/designer\/start\/([^/]+)\/([^/]+)\/?$/)
    if (legacyStart && !DESIGNER_FLOW_TYPE_SEGMENTS.has(legacyStart[1])) {
        return true
    }

    const legacyPersonalize = pathname.match(/^\/designer\/personalize\/([^/]+)\/?$/)
    if (legacyPersonalize && !DESIGNER_FLOW_TYPE_SEGMENTS.has(legacyPersonalize[1])) {
        return true
    }

    return false
}

export type EcommerceDesignerRouteLike = {
    path: string
    params?: Record<string, string | string[] | undefined>
}

/** Route-aware check — honors `:type === 'purchase'` on typed designer pages. */
export function isEcommerceDesignerRoute(route: EcommerceDesignerRouteLike): boolean {
    const rawType = route.params?.type
    const type = Array.isArray(rawType) ? rawType[0] : rawType
    if (type === 'purchase') {
        return true
    }
    return isEcommerceDesignerPath(route.path)
}
